'use client';

import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { IconButton } from '@ui/button';
import { PillText } from '@web/atoms';
import { IMAGE_SIZES, ImageCardGroup, ResponsiveImage } from '@web/molecules';
import { CMSBlok } from '@web/cms';
import { ApiResult } from '../../api';
import { useReporter } from '../../reporter';
import { useSessionCustomer } from '../../customer';
import { CheckoutBar } from '../CheckoutBar';
import {
  Wrapper,
  StyledForm,
  MobileImageContainer,
  DesktopImageContainer,
  DesktopPillContainer,
  MobilePillContainer,
  FormContainer,
  Heading,
  Subheading,
  FieldWrapper,
  ButtonContainer,
} from './styles.js';
import {
  userContinuesFromGWPStep,
  userSelectsGWP,
  userViewsGWPSelectionPage,
} from './events';

export const GiftWithPurchaseForm = ({
  headline,
  imagePillText,
  imagePillVariant = 'success',
  subheadline,
  defaultValues,
  recommendedChoice,
  goBack,
  onSubmit,
  choices,
  mobileImage,
  desktopImage,
  ctaButtonText = 'Continue',
  ctaButtonVariant = 'primary',
  body,
  isRouteChanging,
}) => {
  const {
    handleSubmit,
    control,
    formState: { isSubmitting, isSubmitted },
    reset,
    getValues,
    watch,
  } = useForm({
    mode: 'onChange',
    defaultValues,
  });

  const reporter = useReporter();
  const { isAppSession } = useSessionCustomer();

  watch('gift');
  const hasGiftSelected = Boolean(getValues('gift'));

  const submitForm = async data => {
    reporter.tag(userContinuesFromGWPStep({ option: data?.gift }));

    const result = await onSubmit(data);
    ApiResult.ifError(result, () =>
      setTimeout(() =>
        reset(getValues(), {
          keepIsSubmitted: false,
        }),
      ),
    );
  };

  const checkoutBarProps = {
    ctaText: ctaButtonText,
    isSubmitDisabled: isSubmitting || !hasGiftSelected || isRouteChanging,
    isBackButtonDisabled: isSubmitting,
    shown: true,
    isSubmitting: isSubmitting || isRouteChanging,
    relativePosition: isAppSession,
    buttonVariant: ctaButtonVariant,
  };

  useEffect(() => {
    reporter.tag(userViewsGWPSelectionPage());
  }, [reporter]);

  return (
    <Wrapper>
      <StyledForm onSubmit={handleSubmit(submitForm)}>
        <ButtonContainer aria-live="polite">
          <IconButton
            aria-label="go to previous step"
            icon={{
              iconName: 'arrow-left',
              size: '24px',
              alt: 'back',
              id: 'prev_step_footer',
              color: 'hero.plum',
            }}
            onClick={goBack}
            disabled={isSubmitting || isSubmitted}
            type="button"
            variant="lilac"
          />
        </ButtonContainer>
        <DesktopImageContainer>
          <ResponsiveImage
            priority={true}
            {...desktopImage}
            height={362}
            width={406}
            sizes={IMAGE_SIZES.halfAtLaptop}
          />
        </DesktopImageContainer>
        <FormContainer>
          {imagePillText && (
            <DesktopPillContainer>
              <PillText
                text={imagePillText}
                variant={imagePillVariant}
              ></PillText>
            </DesktopPillContainer>
          )}
          <Heading>{headline}</Heading>
          <MobileImageContainer>
            {imagePillText && (
              <MobilePillContainer>
                <PillText
                  text={imagePillText}
                  variant={imagePillVariant}
                ></PillText>
              </MobilePillContainer>
            )}
            <ResponsiveImage
              priority={true}
              {...mobileImage}
              width={400}
              sizes={IMAGE_SIZES.halfAtLaptop}
            />
          </MobileImageContainer>
          <Subheading>{subheadline}</Subheading>
          <FieldWrapper>
            <Controller
              control={control}
              name="gift"
              render={({ field: { ref, ...field } }) => (
                <>
                  <ImageCardGroup
                    {...field}
                    choices={choices}
                    direction="column"
                    name="gift"
                    legendText="Select your gift"
                    textDirection="row"
                    cardTitleColor="secondary.plum"
                    cardDescriptionColor="text.primary"
                    checkedCardBackgroundColor="background.checked"
                    checkedCardBorderColor="secondary.plum"
                    cardGap={2}
                    recommendedValue={recommendedChoice?.value}
                    onChange={value => {
                      field.onChange(value);
                      reporter.tag(userSelectsGWP({ option: value }));
                    }}
                    disabledPillText="OUT OF STOCK"
                  />
                </>
              )}
            />
          </FieldWrapper>
        </FormContainer>
        <CheckoutBar {...checkoutBarProps} />
      </StyledForm>
      {Boolean(body?.length)
        ? body?.map(content => <CMSBlok key={content._uid} content={content} />)
        : ''}
    </Wrapper>
  );
};

GiftWithPurchaseForm.displayName = 'GiftWithPurchaseForm';

GiftWithPurchaseForm.propTypes = {
  headline: PropTypes.string.isRequired,
  subheadline: PropTypes.string,
  mobileImage: PropTypes.object.isRequired,
  desktopImage: PropTypes.object.isRequired,
  imagePillText: PropTypes.string,
  imagePillVariant: PropTypes.string,
  recommendedChoice: PropTypes.object,
  defaultValues: PropTypes.shape({ gift: PropTypes.string }).isRequired,
  goBack: PropTypes.func.isRequired,
  choices: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      image: PropTypes.object,
      disabled: PropTypes.bool,
    }),
  ).isRequired,
  onSubmit: PropTypes.func.isRequired,
  ctaButtonText: PropTypes.string,
  ctaButtonVariant: PropTypes.string,
  body: PropTypes.array,
};
